import { useSelector } from "react-redux";
import { RootStatePlatform } from "../store/platform-slice";
import { RootStateAnswers } from "../../questionnaire/store/answers-slice";

/**
 * The client's gender as far as the sexual-wellness split is concerned.
 * "unknown" is a real state — no source has answered yet — and must never be
 * collapsed into one of the two, or a system silently disappears.
 */
export type ClientGender = "male" | "female" | "unknown";

/**
 * Resolves the gender from the two shapes the backend uses:
 *
 *  - the report's own `dashboard.gender`, a "Male" / "Female" string;
 *  - the numeric 1 = Male / 2 = Female of the biometric questionnaire, which
 *    the client list (GetCustomers `gender`, 0 when unanswered) shares.
 *
 * The report wins because it ships in the same payload as `score.systems[]` —
 * the thing being filtered — so the two can never disagree. Numbers arrive as
 * strings often enough that the comparison is deliberately loose.
 */
export const resolveClientGender = (
  reportGender?: string | null,
  numericGender?: number | string | null
): ClientGender => {
  const fromReport = String(reportGender ?? "").toLowerCase();
  if (fromReport === "male") return "male";
  if (fromReport === "female") return "female";

  const numeric = Number(numericGender);
  if (numeric === 1) return "male";
  if (numeric === 2) return "female";

  return "unknown";
};

/**
 * True when a system belongs to the gender the client isn't — i.e. Sexual
 * Wellness (Male) = 11 for a female client and (Female) = 12 for a male one.
 * While the gender is unknown neither is hidden: showing an extra card is a
 * far smaller failure than hiding a score that is waiting for approval.
 */
export const isOppositeGenderSystem = (
  system: number,
  gender: ClientGender
): boolean => {
  if (gender === "male") return system === 12;
  if (gender === "female") return system === 11;
  return false;
};

/**
 * The client gender for whichever client the current screen is showing.
 * Reads the report first, then the questionnaire answers — screens that load
 * the report but not the questionnaire (the Health overview) would otherwise
 * see the answers slice's empty default and treat every client as female.
 */
export const useClientGender = (): ClientGender => {
  const reportGender = useSelector(
    (state: RootStatePlatform) =>
      (state.platform.dataPlatformServer as any)?.dashboard?.gender
  );
  const questionnaireGender = useSelector(
    (state: RootStateAnswers) => state.answers.biometricQuest.myAnswers.gender
  );
  return resolveClientGender(reportGender, questionnaireGender);
};
